/**
 * UC-176 — Inline associate panel, anchored to the row action (not a modal).
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatEveId } from '@/shared/variantMapping/formatEveId';
import type { VariantManagementRow } from '@/shared/types/variantMapping';
import {
  ASSOCIATE_MODAL_TITLE,
  ASSOCIATE_NOT_FOUND_MESSAGE,
} from '@/shared/variantMapping/variantAssociation';

export interface AssociateVariantDialogProps {
  open: boolean;
  row: VariantManagementRow | null;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (identifierValue: string) => void;
}

const AssociateVariantDialog: React.FC<AssociateVariantDialogProps> = ({
  open,
  row,
  isSubmitting = false,
  errorMessage = null,
  onCancel,
  onConfirm,
}) => {
  const [identifierValue, setIdentifierValue] = useState('');

  useEffect(() => {
    if (!open) {
      setIdentifierValue('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isSubmitting) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, isSubmitting, onCancel]);

  if (!open || !row) return null;

  const eveId = formatEveId(row.eveId);

  return (
    <div data-testid="associate-variant-dialog" className="vm-design__assoc">
      <div className="vm-design__assoc-head">
        <div className="vm-design__assoc-head-copy">
          <div className="vm-design__assoc-title">{ASSOCIATE_MODAL_TITLE}</div>
          <div className="vm-design__assoc-lede" data-testid="associate-variant-row-context">
            Paste any variant that already sits in the row you want to join. Every identifier in{' '}
            <strong>{eveId}</strong> moves into that row — nothing is deleted.
          </div>
        </div>
        <button
          type="button"
          className="vm-design__assoc-close"
          data-testid="associate-variant-close"
          aria-label="Close"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          ×
        </button>
      </div>
      <div className="vm-design__assoc-body">
        <Input
          id="associate-variant-identifier"
          data-testid="associate-variant-identifier"
          className="vm-design__assoc-input"
          value={identifierValue}
          onChange={(event) => setIdentifierValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onConfirm(identifierValue);
            }
          }}
          placeholder="e.g. 16FT-BLACK"
          disabled={isSubmitting}
          autoFocus
        />
        {errorMessage ? (
          <p data-testid="associate-variant-error" className="vm-design__assoc-error" role="alert">
            {errorMessage}
          </p>
        ) : null}
        <p className="vm-design__assoc-hint">
          If the joined row needs more slots than the table shows, a column is added to fit them.
        </p>
      </div>
      <div className="vm-design__assoc-foot">
        <Button
          type="button"
          variant="outline"
          className="vm-design__assoc-btn"
          data-testid="associate-variant-cancel"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          className="vm-design__assoc-btn"
          data-testid="associate-variant-confirm"
          disabled={isSubmitting}
          onClick={() => onConfirm(identifierValue)}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export { ASSOCIATE_NOT_FOUND_MESSAGE };
export default AssociateVariantDialog;
